// API服务层 - 封装所有后端 FastAPI API调用
const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

// ==================== 认证服务 ====================
export const authService = {
  // 注册
  async signUp(email: string, password: string, userData: any) {
    const response = await fetch(`${BASE_URL}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email,
        password,
        ...userData
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Sign up failed');
    }

    // 注册后通常需要手动登录或后端直接返回 token
    // 这里我们假设需要手动登录，或者根据后端返回处理
    const data = await response.json();
    return { user: data };
  },

  // 登录
  async signIn(email: string, password: string) {
    // FastAPI OAuth2 预期 form-data
    const params = new URLSearchParams();
    params.append('username', email);
    params.append('password', password);

    const response = await fetch(`${BASE_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: params,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Login failed');
    }

    const data = await response.json(); // { access_token, token_type }
    localStorage.setItem('access_token', data.access_token);
    return { user: await this.getCurrentUser(), access_token: data.access_token };
  },

  // 登出
  async signOut() {
    localStorage.removeItem('access_token');
  },

  // 获取当前用户
  async getCurrentUser() {
    const token = localStorage.getItem('access_token');
    if (!token) return null;

    const response = await fetch(`${BASE_URL}/auth/me`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });

    if (!response.ok) {
      // 不在此处删除 token：避免与 useAuth 初始化竞态导致刚进受保护路由时被误判未登录。
      // 退出登录请使用 signOut；无效 token 将在后续需要鉴权的请求中失败。
      return null;
    }

    return response.json();
  },

  // 更新用户资料
  async updateProfile(userId: string, updates: any) {
    const token = localStorage.getItem('access_token');
    const response = await fetch(`${BASE_URL}/users/me`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(updates),
    });

    if (!response.ok) throw new Error('Update profile failed');
    return response.json();
  },

  // 修改密码
  async changePassword(passwords: any) {
    const token = localStorage.getItem('access_token');
    const response = await fetch(`${BASE_URL}/users/me/password`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(passwords),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Change password failed');
    }
    return response.json();
  },

  // 重置/轮转 API Key
  async rotateApiKey() {
    const token = localStorage.getItem('access_token');
    const response = await fetch(`${BASE_URL}/users/me/api-key/rotate`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
    });

    if (!response.ok) throw new Error('Rotate API Key failed');
    return response.json();
  },
};

// ==================== 其他服务 (保持占位或对应实现) ====================
// 这里暂时只实现核心的 auth，其他可以根据需要逐步迁移
export const ipAnalysisService = {
  async generateIPPositioning(input: any) {
     // TODO: Implement
     return {};
  }
};

export const contentService = {
    // 这里的 contentService 在 src/services/contentService.ts 已经有独立实现了
};

export const eventService = {
  // 上传文件
  async uploadFile(file: File) {
    const token = localStorage.getItem('access_token');
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${BASE_URL}/api/upload/`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData,
    });

    if (!response.ok) throw new Error('Upload failed');
    return response.json();
  },
};

export default {
  auth: authService,
  ipAnalysis: ipAnalysisService,
  event: eventService,
};
